import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Hexagon, ArrowLeft, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { API } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 honeycomb-bg opacity-20" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm"
      >
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm mb-8 transition-colors"
          data-testid="forgot-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="relative">
            <Hexagon className="w-10 h-10 text-amber-500 fill-amber-500/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
            </div>
          </div>
          <div>
            <h1 className="font-heading font-black text-2xl text-white">Forgot Password</h1>
            <p className="text-sm text-zinc-500">We'll send you a reset link</p>
          </div>
        </div>

        {sent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-xl p-6 text-center"
          >
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Mail className="w-6 h-6 text-emerald-400" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2">Check your email</h2>
            <p className="text-sm text-zinc-400 mb-4">
              If an account exists for <span className="text-white">{email}</span>, we've sent a password reset link. It expires in 1 hour.
            </p>
            <p className="text-xs text-zinc-600">Don't see it? Check your spam folder.</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 text-sm text-amber-500 hover:text-amber-400 font-medium"
              data-testid="forgot-back-to-login"
            >
              Back to login
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400" data-testid="forgot-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-sm text-zinc-400">Email address</Label>
              <Input
                id="forgot-email"
                data-testid="forgot-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 focus:border-amber-500/50 focus:ring-amber-500/20"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="honey-btn w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50"
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
